<template>
  <div class="content">
    <!-- Nagłówek białej listy -->
    <h3 class="center-aligned">{{ t('white-list').toUpperCase() }}:</h3>
    <div class="form center-aligned">
      <!-- Formularz ręcznego dodawania pojazdu -->
      <div class="form-group">
        <label for="licensePlate" class="label-license-plate">{{ t('plate-no') }}:</label>
        <input id="licensePlate" v-model="licensePlate" type="text" />
      </div>
      <div class="form-group">
        <label for="description" class="label-description">{{ t('vehicle-description') }}:</label>
        <input id="description" v-model="description" type="text" maxlength="100" />
      </div>
      <!-- Przypisywanie użytkownika tylko dla osób z dostępem -->
      <div v-if="hasAccess('UserManagement')" class="form-group">
        <label for="user" class="label-user">Przypisz użytkownika:</label>
        <select id="user" v-model="selectedUserId">
          <option value="">Przypisz użytkownika</option>
          <option v-for="user in users"
                  :key="user.id"
                  :value="user.id">
            {{ user.username }}
          </option>
        </select>
      </div>
      <div class="form-group">
        <label for="groupSearch" class="label-group">Grupy kontaktów:</label>
        <div class="group-selector">
          <input
            id="groupSearch"
            v-model="groupSearch"
            type="text"
            placeholder="Szukaj grupy..."
          />
          <div class="group-options">
            <label v-for="group in filteredContactGroups" :key="group.id" class="group-option">
              <input v-model="selectedContactGroupIds" type="checkbox" :value="group.id" />
              {{ group.name }}
            </label>
            <p v-if="filteredContactGroups.length === 0" class="group-empty">Brak grup kontaktów.</p>
          </div>
        </div>
      </div>
      <!-- Przycisk dodawania pojazdu -->
      <button @click="addPlate" :disabled="!licensePlate" class="button-save">{{ t('add') }}</button>
      
      <div>
        {{ t('or') }}
      </div>
      
      <!-- Wgrywanie pojazdów z pliku CSV -->
      <div class="file-upload center-aligned">
        <input type="file" @change="handleFileUpload" accept=".csv" ref="fileInput"/>
        <div>
          <span v-if="uploadedFileName">{{ uploadedFileName }} <button @click="removeFile" class="button-delete">X</button></span>
        </div>

        <!-- Tabela pojazdów z pliku CSV gotowych do wysyłki -->
        <div v-if="csvVehicles.length > 0">
          <h4>{{ t('ready-to-send-csv-vehicles') }}:</h4>
          <table class="csv-vehicle-table center-aligned">
            <thead>
              <tr>
                <th>{{ t('ordinal-number') }}</th>
                <th>{{ t('short-plate-no') }}</th>
                <th v-if="hasAccess('UserManagement')">Przypisany użytkownik</th>
                <th>{{ t('vehicle-description') }}</th>
                <th>{{ t('action') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(vehicle, index) in csvVehicles" :key="vehicle.id">
                <td>{{ index + 1 }}</td>
                <td>{{ vehicle.licensePlate }}</td>
                <td v-if="hasAccess('UserManagement')">
                  <select :id="`user-${vehicle.id}`" v-model="vehicle.userId">
                    <option value="">Przypisz użytkownika</option>
                    <option v-for="user in users"
                            :key="user.id"
                            :value="user.id">
                      {{ user.username }}
                    </option>
                  </select>
                </td>
                <td>{{ vehicle.description }}</td>
                <td><button @click="removeCSVVehicle(index)" class="button-delete">{{ t('delete') }}</button></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Przycisk do wysyłki wszystkich pojazdów z CSV -->
      <button v-if="csvVehicles.length > 0" @click="submitCSVVehicles" class="button-save">{{ t('add-vehicles-from-csv') }}</button>
      <div class="status-wrap">
        <div
          v-if="statusMessage && !statusDismissed"
          class="alert alert-dismissible fade show mt-2"
          :class="statusType === 'success' ? 'alert-success' : 'alert-danger'"
          role="alert"
        >
          {{ statusMessage }}
          <button
            type="button"
            class="btn-close"
            aria-label="Close"
            @click="dismissStatus"
          ></button>
        </div>
      </div>
    </div>

    <!-- Tabela pojazdów na białej liście -->
    <div v-if="vehicles.length > 0">
      <table class="vehicle-table center-aligned">
        <thead>
          <tr>
            <th>{{ t('ordinal-number') }}</th>
            <th>{{ t('short-plate-no') }}</th>
            <th>Przypisany użytkownik</th>
            <th>{{ t('hits-no') }}</th>
            <th>{{ t('monitored-since') }}</th>
            <th>{{ t('vehicle-description') }}</th>
            <th>{{ t('action') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(vehicle, index) in vehicles" :key="vehicle.id">
            <td>{{ index + 1 }}</td>
            <td>{{ vehicle.licensePlate }}</td>
            <td>{{ vehicle.username }}</td>
            <td>{{ vehicle.hits }}</td>
            <td>{{ formatDateTime(vehicle.monitoredSince) }}</td>
            <td>{{ vehicle.description }}</td>
            <td><button @click="removeVehicle(vehicle.id)" class="button-delete">{{ t('delete') }}</button></td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-else>
      <p class="center-aligned">{{ t('no-vehicles-on-white-list') }}</p>
    </div>
  </div>
</template>

<script>
import WhiteListsService from "../services/WhiteListsService";
import Papa from 'papaparse'; // Biblioteka do parsowania CSV
import { useUserStore } from "@/stores/user.store";
import ContactGroupsService from "@/services/ContactGroupsService";

export default {
  name: 'WhiteList',
  data() {
    return {
      licensePlate: '',      // Wartość pola numer rejestracyjny
      description: '',       // Wartość pola opis
      vehicles: [],          // Lista wszystkich pojazdów na białej liście
      csvVehicles: [],       // Lista pojazdów wgranych z CSV
      uploadedFileName: '',  // Nazwa wgranego pliku CSV
      currentPage: 1,
      pageSize: 5,
      users: [],             // Lista użytkowników do przypisywania pojazdu
      selectedUserId: null,  // Wybrany użytkownik w formularzu dodawania
      contactGroups: [],
      selectedContactGroupIds: [],
      groupSearch: '',
      username: '',
      statusMessage: '',
      statusType: 'error',
      statusDismissed: false,
    };
  },
  computed: {
    filteredContactGroups() {
      const phrase = this.groupSearch.trim().toLowerCase();
      if (!phrase) {
        return this.contactGroups;
      }

      return this.contactGroups.filter(group =>
        String(group.name ?? '').toLowerCase().includes(phrase)
      );
    }
  },
  methods: {
    normalizePlate(value) {
      return String(value ?? '').trim().toUpperCase();
    },
    normalizeDescription(value) {
      return String(value ?? '').trim();
    },
    mapVehicleFromApi(vehicle) {
      return {
        licensePlate: vehicle.plateLpr,
        description: vehicle.description ?? '',
        hits: vehicle.hitCounter ?? 0,
        monitoredSince: vehicle.createdAt || new Date().toISOString(),
        id: vehicle.id,
        userId: vehicle.user?.id ?? null,
        username: vehicle.user?.username ?? '-'
      };
    },
    parseApiError(error, fallbackMessage) {
      if (typeof error === 'string' && error.trim() !== '') {
        return error;
      }
      if (error?.message) {
        return error.message;
      }
      return fallbackMessage;
    },
    isNotFoundError(error) {
      return error === 404 || error === '404' || error?.status === 404 || error?.response?.status === 404;
    },
    // Sprawdza uprawnienia użytkownika
    hasAccess(id) {
      const userStore = useUserStore();
      return userStore.hasAccess(id);
    },
    // Pobiera użytkowników do przypisywania pojazdów
    async getUsers() {
      try {
        const response = await WhiteListsService.getUsers();
        this.users = (Array.isArray(response) ? response : []).map(u => ({
          id:       u.id,
          username: u.username
        }));
      } catch (error) {
        console.error('Error fetching users:', error);
        this.users = [];
      }
    },
    async getContactGroups() {
      try {
        const response = await ContactGroupsService.getAll();
        this.contactGroups = (Array.isArray(response) ? response : []).map(group => ({
          id: group.id,
          name: group.name
        }));
      } catch (error) {
        console.error('Error fetching contact groups:', error);
        this.contactGroups = [];
      }
    },
    // Dodaje pojedynczy pojazd do białej listy (z formularza)
    async addPlate() {
      const normalizedPlate = this.normalizePlate(this.licensePlate);
      const normalizedDescription = this.normalizeDescription(this.description);

      if (!normalizedPlate) {
        this.showStatus('Numer rejestracyjny jest wymagany.', 'error');
        return;
      }

      try {
        const response = await WhiteListsService.postNewPlate(
          normalizedPlate,
          normalizedDescription,
          this.selectedUserId,
          this.selectedContactGroupIds
        );

        this.vehicles.unshift(this.mapVehicleFromApi(response));
        // Czyści formularz
        this.licensePlate = '';
        this.description = '';
        this.selectedUserId = null;
        this.selectedContactGroupIds = [];
        this.groupSearch = '';
        this.showStatus('Pojazd został dodany do białej listy.', 'success');

      } catch (error) {
        console.error('Error adding vehicle:', error);
        this.showStatus(this.parseApiError(error, 'Nie udało się dodać pojazdu.'), 'error');
      }
    },
    // Pobiera wszystkie pojazdy z białej listy z backendu
    async fetchVehicles() {
      try {
        const response = await WhiteListsService.getAll();
        this.vehicles = (Array.isArray(response) ? response : []).map(this.mapVehicleFromApi);
      } catch (error) {
        if (this.isNotFoundError(error)) {
          this.vehicles = [];
          this.statusMessage = '';
          this.statusDismissed = false;
          return;
        }
        console.error('Error fetching vehicles:', error);
        this.vehicles = [];
      }
    },
    // Usuwa pojazd z białej listy po ID
    async removeVehicle(id) {
      try {
        await WhiteListsService.delete(id);
        this.vehicles = this.vehicles.filter(vehicle => vehicle.id !== id);
        this.showStatus('Pojazd został usunięty z białej listy.', 'success');
      } catch (error) {
        console.error('Error deleting vehicle:', error);
        this.showStatus('Nie udało się usunąć pojazdu.', 'error');
      }
    },
    // Paginacja - poprzednia strona
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    // Paginacja - następna strona
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    // Formatowanie daty i godziny na czytelny zapis
    formatDateTime(dateString) {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');

      return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
    },
    // Obsługa wgrywania pliku CSV z pojazdami
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.uploadedFileName = file.name; // Ustaw nazwę pliku
        Papa.parse(file, {
          header: false,
          skipEmptyLines: true,
          delimiter: ';', // Ustaw delimiter na średnik
          complete: this.addVehiclesFromCSV,
          error: (error) => {
            console.error('Error reading CSV file:', error);
            this.showStatus('Nie udało się odczytać pliku CSV.', 'error');
          }
        });
      }
    },
    // Dodaje pojazdy z pliku CSV do tymczasowej listy (do potwierdzenia wysyłki)
    addVehiclesFromCSV(results) {
      this.csvVehicles = results.data
        .map(vehicle => ({
        licensePlate: this.normalizePlate(vehicle[0]),
        description: this.normalizeDescription(vehicle[1]),
        hits: 0,
        monitoredSince: new Date().toISOString(),
        id: Date.now() + Math.random(), // Unikalny ID dla pojazdu z CSV
        userId: null,
        contactGroupIds: [...this.selectedContactGroupIds]
      }))
      .filter(vehicle => vehicle.licensePlate);
    },
    // Wysyła wszystkie pojazdy z CSV do backendu
    async submitCSVVehicles() {
      let failed = false;
      for (let vehicle of this.csvVehicles) {
        try {
          if (!vehicle.licensePlate) {
            failed = true;
            continue;
          }
          const response = await WhiteListsService.postNewPlate(
            vehicle.licensePlate,
            vehicle.description,
            vehicle.userId,
            vehicle.contactGroupIds ?? []
          );

          this.vehicles.unshift(this.mapVehicleFromApi(response));

        } catch (error) {
          console.error('Error adding vehicle from CSV:', error);
          failed = true;
        }
      }
      // Czyści tymczasową listę po wysłaniu
      this.csvVehicles = [];
      this.uploadedFileName = '';
      this.$refs.fileInput.value = '';
      this.showStatus(
        failed
          ? 'Część pojazdów z CSV nie została dodana.'
          : 'Pojazdy z pliku CSV zostały dodane.',
        failed ? 'error' : 'success'
      );
    },
    // Usuwa plik CSV i listę pojazdów z CSV (reset)
    removeFile() {
      this.uploadedFileName = '';
      this.csvVehicles = [];
      this.$refs.fileInput.value = '';
    },
    // Usuwa pojedynczy pojazd z tymczasowej listy CSV
    removeCSVVehicle(index) {
      this.csvVehicles.splice(index, 1);
    },
    showStatus(message, type = 'error') {
      this.statusMessage = message;
      this.statusType = type;
      this.statusDismissed = false;
    },
    dismissStatus() {
      this.statusDismissed = true;
    },
  },
  // Po zamontowaniu komponentu pobiera użytkowników i pojazdy z API
  mounted() {
    this.getUsers();
    this.getContactGroups();
    this.fetchVehicles();
  }
};
</script>
  
<style scoped>
h3 {
  padding: 1rem;
  font-weight: bold;
  border-bottom: 1px solid #aeaeae;
}

/* .form {
  margin-bottom: 20px;
  margin-top: 1.5rem;
  border-bottom: 1px solid #aeaeae;
}
*/
.form .form-group {
  margin-bottom: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding-right: 10rem;
}

.form .form-group .label-license-plate {
  padding-right: 0.5rem;
}

.form .form-group .label-description {
  padding-right: 3.77rem;
}

.form .form-group select {
  padding: 0.5rem;
  border: 1px solid #ccc;
  background-color: #f0f0f0;
  color: black;
  cursor: pointer;
}

.form .form-group .label-group {
  padding-right: 1rem;
}

.group-selector {
  width: 300px;
}

.group-selector input[type="text"] {
  width: 100%;
  padding: 0.45rem;
  border: 1px solid #ccc;
}

.group-options {
  max-height: 150px;
  overflow-y: auto;
  border: 1px solid #ccc;
  border-top: none;
  text-align: left;
  background: #fff;
}

.group-option {
  display: block;
  padding: 0.3rem 0.5rem;
}

.group-empty {
  margin: 0;
  padding: 0.5rem;
  font-size: 0.9rem;
}

/* .form button {
  margin-top: 1rem;
  width: 194px;
  padding: 0.5rem;
  border: 1px solid #ccc;
  background-color: #f0f0f0;
  color: black;
  cursor: pointer;
  transition: background-color 0.3s, color 0.3s, transform 0.3s;
  margin-bottom: 1.5rem;
} */

.vehicle-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
}

.vehicle-table th,
.vehicle-table td {
  border: 1px solid #ccc;
  padding: 10px;
}

.vehicle-table td button {
  width: 75%;
  border: 1px solid #ccc;
  background-color: #f0f0f0;
  color: black;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  transition: background-color 0.3s, color 0.3s, transform 0.3s;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
}

.pagination button {
  padding: 5px 10px;
  margin: 0 5px;
}

p {
  font-size: large;
}

.file-upload {
  margin: 20px 0;
}

.file-upload span {
  margin-left: 10px;
}

.file-upload button {
  background: none;
  border: none;
  color: red;
  cursor: pointer;
  font-size: 1rem;
  padding: 0;
}

.csv-vehicle-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
}

.csv-vehicle-table th,
.csv-vehicle-table td {
  border: 1px solid #ccc;
  padding: 10px;
  /* text-align: center; */
}

.csv-vehicle-table td button {
  width: 75%;
  border: 1px solid #ccc;
  background-color: #f0f0f0;
  color: black;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  transition: background-color 0.3s, color 0.3s, transform 0.3s;
}

.alert-success {
  color: #155724;
  background-color: #d4edda;
  border-color: #c3e6cb;
}

.status-wrap {
  width: 55%;
  margin: 0.5rem auto 0;
}

.status-wrap .alert {
  margin-bottom: 0.5rem;
}

.status-wrap .btn-close {
  box-sizing: content-box;
  width: 1em;
  height: 1em;
  padding: 0.25em;
  color: #000;
  background: transparent
    url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23000'%3e%3cpath d='M.293.293a1 1 0 011.414 0L8 6.586 14.293.293a1 1 0 111.414 1.414L9.414 8l6.293 6.293a1 1 0 01-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 01-1.414-1.414L6.586 8 .293 1.707a1 1 0 010-1.414z'/%3e%3c/svg%3e")
    center/1em auto no-repeat;
  border: 0;
  border-radius: 0.375rem;
  opacity: 0.5;
}

.status-wrap .alert-dismissible .btn-close {
  right: 0.75rem;
}

.status-wrap .btn-close:hover {
  opacity: 0.75;
}

.button-save {
  border-radius: 4px;
}

.button-save:hover {
  background-color: #005511 !important;
  border: 1px solid #005511 !important;
}

.button-delete {
  padding: 0.45rem 1.6rem 0.5rem 1rem;

  border-radius: 4px;
}

.button-delete:hover {
  background-color: #d13f3f !important;
  color: white !important;
}
</style>
